<template>
  <div class="card">
    <div class="card-header">
      <h1 class="card-title">
        Party Created
      </h1>
    </div>
    <div class="card-body">
      <div class="row mb-4">
        <div class="col-4">
          <div class="text-center p-3 bg-dark rounded">
            <h5 class="text-warning">⏳ Status</h5>
            <p class="mb-0">{{ isConnected ? 'Connected' : 'Connecting...' }}</p>
          </div>
        </div>
        <div class="col-4">
          <div class="text-center p-3 bg-dark rounded">
            <h5 class="text-info">👥 Players</h5>
            <p class="mb-0">{{ connectedPlayers.size }} / {{ partyData?.playerLimit }}</p>
          </div>
        </div>
        <div class="col-4">
          <div class="text-center p-3 bg-dark rounded">
            <h5 class="text-success">🎮 GameMaster</h5>
            <p class="mb-0">Active</p>
          </div>
        </div>
      </div>

      <h5>Connected Players</h5>
      <div class="bg-dark p-3 rounded mb-3">
        <div v-if="connectedPlayers.size === 0" class="text-muted text-center">
          <i class="bi bi-people"></i>
          No players connected yet. Share the party code with players.
        </div>
        <div v-else>
          <div
            v-for="[playerId, player] in connectedPlayers"
            :key="playerId"
            class="d-flex justify-content-between align-items-center border-bottom py-2"
          >
            <div class="d-flex align-items-center">
              <div class="player-avatar me-3">
                <img
                  v-if="player.avatar && player.avatar.includes('//')"
                  :src="player.avatar"
                  :alt="player.name + ' avatar'"
                  class="rounded-circle"
                  style="width: 40px; height: 40px; object-fit: cover;"
                />
                <div
                  v-else-if="player.avatar"
                  class="avatar-emoji rounded-circle d-flex align-items-center justify-content-center"
                  style="width: 40px; height: 40px; background-color: #f8f9fa; border: 1px solid #dee2e6; font-size: 1.5rem;"
                >
                  {{ player.avatar }}
                </div>
                <div
                  v-else
                  class="avatar-placeholder rounded-circle d-flex align-items-center justify-content-center"
                  style="width: 40px; height: 40px; background-color: #6c757d; color: white; font-size: 1.2rem;"
                >
                  <i class="bi bi-person"></i>
                </div>
              </div>
              <div>
                <strong>{{ player.name }}</strong>
                <div class="small text-muted">
                  <span v-if="player.pseudo && player.pseudo !== player.name">Pseudo: {{ player.pseudo }}</span>
                  <span v-if="player.firstName">Prénom: <strong>{{ player.firstName }}</strong></span>
                  <br><span>ID: {{ player.id }}</span>
                </div>
              </div>
            </div>
            <span :class="[player.isReady ? 'text-success' : 'text-warning']">
              <i data-v-5171686b="" :class="player.isReady ? 'bi bi-check-circle-fill' : 'bi bi-hourglass-split'"></i>
              &nbsp;
              {{ player.isReady ? 'Ready' : 'Loading...' }}
            </span>
          </div>
        </div>
      </div>

      <h5>Player Join Information</h5>
      <div class="input-group mb-2">
        <input type="text" class="form-control" :value="joinUrl" readonly>
        <button
          class="btn btn-outline-secondary btn-lg"
          type="button"
          @click="copyJoinUrl"
          :disabled="copyStatus === 'copying'"
        >
          <i :class="copyIconClass"></i>
        </button>
        <button
          class="btn btn-outline-secondary btn-lg"
          type="button"
          @click="openJoinUrl"
        >
          <i class="bi bi-box-arrow-up-right"></i>
        </button>
      </div>
      <div v-if="copyStatus === 'success'" class="text-success small mb-2">
        <i class="bi bi-check-circle"></i> URL copied to clipboard!
      </div>
      <div v-if="copyStatus === 'error'" class="text-danger small mb-2">
        <i class="bi bi-exclamation-circle"></i> Failed to copy. Please copy manually.
      </div>
      <br/>

      <h5>TV Join Information</h5>
      <div class="input-group mb-2">
        <input type="text" class="form-control" :value="tvJoinUrl" readonly>
        <button
          class="btn btn-outline-secondary btn-lg"
          type="button"
          @click="copyTvJoinUrl"
          :disabled="tvCopyStatus === 'copying'"
        >
          <i :class="tvCopyIconClass"></i>
        </button>
        <button
          class="btn btn-outline-secondary btn-lg"
          type="button"
          @click="openTvJoinUrl"
        >
          <i class="bi bi-box-arrow-up-right"></i>
        </button>
      </div>
      <div v-if="tvCopyStatus === 'success'" class="text-success small mb-2">
        <i class="bi bi-check-circle"></i> URL copied to clipboard!
      </div>
      <div v-if="tvCopyStatus === 'error'" class="text-danger small mb-2">
        <i class="bi bi-exclamation-circle"></i> Failed to copy. Please copy manually.
      </div>
      <br/>

      <h5>Party Controls</h5>
      <div class="d-grid gap-2 mb-3">
        <button
          @click="$emit('start-game')"
          :disabled="!allPlayersReady && connectedPlayers.size === 0"
          :class="['btn', allPlayersReady ? 'btn-success' : 'btn-secondary']"
        >
          <i class="bi bi-play me-2"></i>
          {{ allPlayersReady ? 'Start Game (All Ready!)' : 'Start Game' }}
        </button>
        <button @click="$emit('test-party-ops')" class="btn btn-info">
          <i class="bi bi-gear me-2"></i>
          Test Party Operations
        </button>
        <button @click="$emit('end-party')" class="btn btn-danger">
          <i class="bi bi-stop me-2"></i>
          End Party
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Player } from '../composables/useSignaling'
import type { PartyData } from '../composables/usePartyState'

// Copy functionality state
const copyStatus = ref<'idle' | 'copying' | 'success' | 'error'>('idle')

// Computed properties for copy button
const copyButtonText = computed(() => {
  switch (copyStatus.value) {
    case 'copying': return 'Copying...'
    case 'success': return 'Copied!'
    case 'error': return 'Failed'
    default: return 'Copy'
  }
})

const copyIconClass = computed(() => {
  switch (copyStatus.value) {
    case 'copying': return 'bi bi-arrow-clockwise spin'
    case 'success': return 'bi bi-check-circle'
    case 'error': return 'bi bi-exclamation-circle'
    default: return 'bi bi-clipboard'
  }
})

interface Props {
  partyData: PartyData | null
  joinUrl: string
  tvJoinUrl: string
  connectedPlayers: Map<string, Player>
  isConnected: boolean
  allPlayersReady: boolean
}

interface Emits {
  (e: 'start-game'): void
  (e: 'test-party-ops'): void
  (e: 'end-party'): void
}

const props = defineProps<Props>()
defineEmits<Emits>()

// Methods
async function copyJoinUrl() {
  copyStatus.value = 'copying'

  try {
    await navigator.clipboard.writeText(props.joinUrl)
    copyStatus.value = 'success'

    // Reset status after 3 seconds
    setTimeout(() => {
      copyStatus.value = 'idle'
    }, 3000)
  } catch (error) {
    console.error('Failed to copy URL:', error)
    copyStatus.value = 'error'

    // Reset status after 5 seconds
    setTimeout(() => {
      copyStatus.value = 'idle'
    }, 5000)
  }
}

function openJoinUrl() {
  window.open(props.joinUrl, '_blank')
}

// TV Copy functionality state
const tvCopyStatus = ref<'idle' | 'copying' | 'success' | 'error'>('idle')
const tvCopyIconClass = computed(() => {
  switch (tvCopyStatus.value) {
    case 'copying': return 'bi bi-arrow-clockwise spin'
    case 'success': return 'bi bi-check-circle'
    case 'error': return 'bi bi-exclamation-circle'
    default: return 'bi bi-clipboard'
  }
})

async function copyTvJoinUrl() {
  tvCopyStatus.value = 'copying'

  try {
    await navigator.clipboard.writeText(props.tvJoinUrl)
    tvCopyStatus.value = 'success'

    // Reset status after 3 seconds
    setTimeout(() => {
      tvCopyStatus.value = 'idle'
    }, 3000)
  } catch (error) {
    console.error('Failed to copy TV URL:', error)
    tvCopyStatus.value = 'error'

    // Reset status after 5 seconds
    setTimeout(() => {
      tvCopyStatus.value = 'idle'
    }, 5000)
  }
}

function openTvJoinUrl() {
  window.open(props.tvJoinUrl, '_blank')
}
</script>

<style scoped>
.player-avatar {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 50%;
  border: 2px solid #dee2e6;
}

.player-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.player-avatar .avatar-emoji {
  font-size: 20px;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
